import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineArrowTrendingUp,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService } from '../../../services';
import masterDataService from '../../../services/masterDataService';
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";

const WorkOrderPrioritiesPage = () => {
  // Get cached data if available
  const getCachedData = () => {
    try {
      const cachedPriorities = sessionStorage.getItem('wo_priorities_data');
      return {
        priorities: cachedPriorities ? JSON.parse(cachedPriorities) : [],
        hasData: !!cachedPriorities,
      };
    } catch {
      return { priorities: [], hasData: false };
    }
  };

  // Initialize with cached data if available
  const cachedInitialData = getCachedData();
  const [priorities, setPriorities] = useState(cachedInitialData.priorities);
  const [loading, setLoading] = useState(!cachedInitialData.hasData);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPriority, setEditingPriority] = useState(null);
  const [formData, setFormData] = useState({
    priorityName: '',
    slaHours: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    const tenantId = authService.getTenantId();
    if (tenantId) {
      // Load fresh data in background if we have cached data, otherwise show loading
      const hasCachedData = cachedInitialData.hasData;
      loadPriorities(!hasCachedData);
    } else {
      setError('Please login to view work order priorities');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPriorities = async (showLoading = true) => {
    const tenantId = authService.getTenantId();
    if (!tenantId) {
      setError('Please login to view work order priorities');
      setLoading(false);
      return;
    }

    try {
      if (showLoading) {
        setLoading(true);
      }
      setError('');
      const data = await masterDataService.getWorkOrderPriorities(tenantId);
      const prioritiesArray = Array.isArray(data) ? data : [];
      setPriorities(prioritiesArray);
      
      // Cache the data
      sessionStorage.setItem('wo_priorities_data', JSON.stringify(prioritiesArray));
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load work order priorities. Please try again.';
      setError(errorMessage);
      console.error('Error loading work order priorities:', err);
      // Don't clear cached data on error, keep showing it
      if (!cachedInitialData.hasData) {
        setPriorities([]);
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredPriorities = priorities.filter((priority) =>
    priority.priorityName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingPriority(null);
    setFormData({
      priorityName: '',
      slaHours: '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (priority) => {
    setEditingPriority(priority);
    setFormData({
      priorityName: priority.priorityName || '',
      slaHours: priority.slaHours?.toString() || '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      // Note: deleteFn is null in config, so we'll skip delete for now
      setError('Delete functionality is not available for work order priorities.');
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete work order priority. Please try again.');
      console.error('Error deleting work order priority:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.priorityName.trim()) {
      errors.priorityName = 'Priority name is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const tenantId = authService.getTenantId();
    if (!tenantId) {
      setError('Please login to save work order priority');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const priorityData = {
        tenantId: tenantId,
        priorityName: formData.priorityName.trim(),
        slaHours: formData.slaHours ? parseFloat(formData.slaHours) : null,
      };

      if (editingPriority) {
        priorityData.priorityId = editingPriority.priorityId;
        await masterDataService.updateWorkOrderPriority(editingPriority.priorityId, priorityData);
      } else {
        await masterDataService.createWorkOrderPriority(priorityData);
      }

      await loadPriorities();
      setShowModal(false);
      setFormData({ priorityName: '', slaHours: '' });
      setEditingPriority(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save work order priority. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPriority(null);
    setFormData({ priorityName: '', slaHours: '' });
    setFormErrors({});
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading work order priorities..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Work Order Priorities</h1>
          <p>Manage work order priorities for Planned Maintenance</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus />
          Add Priority
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search priorities..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredPriorities.length} {filteredPriorities.length === 1 ? 'priority' : 'priorities'}
          </div>
        </div>

        {filteredPriorities.length === 0 ? (
          <div className="empty-state">
            <HiOutlineArrowTrendingUp className="empty-icon" />
            <h3>No priorities found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first work order priority'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Priority
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Priority Name</th>
                <th>SLA (hours)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPriorities.map((priority) => (
                <tr key={priority.priorityId}>
                  <td>{priority.priorityId}</td>
                  <td className="name-cell">{priority.priorityName}</td>
                  <td>{priority.slaHours ?? '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(priority)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingPriority ? 'Edit Work Order Priority' : 'Add New Work Order Priority'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="wo-priority-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingPriority
                  ? 'Updating...'
                  : 'Creating...'
                : editingPriority
                ? 'Update'
                : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="wo-priority-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="priorityName">
                Priority Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="priorityName"
                value={formData.priorityName}
                onChange={(e) =>
                  setFormData({ ...formData, priorityName: e.target.value })
                }
                className={formErrors.priorityName ? 'error' : ''}
                placeholder="Enter priority name"
              />
              {formErrors.priorityName && (
                <span className="field-error">{formErrors.priorityName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="slaHours">SLA Hours</label>
              <input
                type="number"
                id="slaHours"
                value={formData.slaHours}
                onChange={(e) =>
                  setFormData({ ...formData, slaHours: e.target.value })
                }
                placeholder="Enter SLA hours (optional)"
                step="0.1"
              />
            </div>
          </form>
        </div>
      </SharedModal>
    </div>
  );
};

export default WorkOrderPrioritiesPage;
